import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

export class MediaGallery extends Component {
  #controller = new AbortController();
  #scrollTicking = false;
  #overlay;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal },
    );

    if (!isMobileBreakpoint()) return;

    this.#createOverlay();
    this.#updateScrollEffect();

    window.addEventListener(
      'scroll',
      this.#handleScroll,
      {
        passive: true,
        signal,
      },
    );

    window.addEventListener(
      'resize',
      this.#updateScrollEffect,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #createOverlay() {
    if (this.#overlay) return;

    this.style.position = 'relative';
    this.style.overflow = 'hidden';

    this.#overlay = document.createElement('div');

    Object.assign(this.#overlay.style, {
      position: 'absolute',
      inset: '0',
      background: 'white',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '999',
    });

    this.appendChild(this.#overlay);
  }

  #handleScroll = () => {
    if (this.#scrollTicking) return;

    this.#scrollTicking = true;

    requestAnimationFrame(() => {
      this.#updateScrollEffect();
      this.#scrollTicking = false;
    });
  };

  #updateScrollEffect = () => {
    const { top, height } = this.getBoundingClientRect();

    const progress = Math.max(
      0,
      Math.min(
        1,
        -top / height,
      ),
    );

    const ratio = 0.8 + (0.3 * progress);

    this.style.setProperty(
      '--gallery-aspect-ratio',
      ratio.toFixed(3),
    );

    const overlayProgress = Math.max(
      0,
      Math.min(
        1,
        (progress - 0.15) / 0.85,
      ),
    );

    if (this.#overlay) {
      this.#overlay.style.opacity = overlayProgress.toFixed(3);
    }
  };

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, {
      animate: false,
    });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia,
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}